import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import { useSeo } from '@/hooks/useSeo';
import { brand, stats, testimonials } from '@/mocks/gossip';
import ContactForm from './components/ContactForm';

const positions = [
  {
    id: 'pos-1',
    icon: 'ri-compass-3-line',
    title: '热点整理者',
    desc: '把分散在各处的话题收拢到一处，省去你在信息流里反复翻找的时间。',
  },
  {
    id: 'pos-2',
    icon: 'ri-chat-smile-2-line',
    title: '评论区的东道主',
    desc: '我们希望评论区是能好好说话的地方，有观点、有补充，而不是互相指责。',
  },
  {
    id: 'pos-3',
    icon: 'ri-gift-line',
    title: '福利的发放方',
    desc: '抽奖、打卡奖励都按公示规则发放，参与一次就够，不需要靠重复操作换概率。',
  },
];

const principles = [
  { id: 'pr-1', title: '先求证，再讨论', desc: '没有可靠信息来源的内容不写成结论，宁可标注为「待确认」，也不抢那一小时的热度。' },
  { id: 'pr-2', title: '不做标题党', desc: '标题要能概括正文，不用夸张词汇制造落差。点进来看到的内容，和标题说的是一回事。' },
  { id: 'pr-3', title: '尊重当事人', desc: '不传播未经证实的私人信息，也不对与内容无关的个人生活做评价。' },
  { id: 'pr-4', title: '规则写清楚', desc: '每一个互动活动都明确列出参与条件、时间与开奖方式，避免事后产生歧义。' },
];

const milestones = [
  { id: 'm1', year: brand.founded, text: '从每天整理一条热搜话题开始，确立「先求证、再讨论」的更新习惯。' },
  { id: 'm2', year: '2024 年', text: '上线评论区打卡机制，连续签到与节点奖励让讨论区逐渐有了固定的常客。' },
  { id: 'm3', year: '2025 年', text: '加入转发抽奖与粉丝投票，把单向阅读变成可以参与的互动。' },
  { id: 'm4', year: '2026 年', text: '累计更新超过一千条话题，互动玩法形成稳定的每周节奏。' },
];

export default function About() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 瓜田福利社',
        description: '瓜田福利社的品牌介绍、网站定位、发展理念、内容原则与联系方式。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '瓜田福利社',
        email: brand.email,
        description: brand.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '关于我们 - 瓜田福利社 品牌介绍与内容原则',
    description:
      '了解瓜田福利社的品牌定位、发展理念与内容原则。我们坚持先求证再讨论、不做标题党，并用清晰的规则运营福利吃瓜抽奖、打卡与投票玩法。',
    keywords: '瓜田福利社,关于我们,福利吃瓜社区,内容原则,联系方式',
    path: '/about',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-16 md:pt-[68px]">
        <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-12 bg-background-100 border-b border-background-200">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs mb-4">
                <i className="ri-emotion-happy-line"></i>
                关于我们
              </span>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                做一间认真吃瓜的小店
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {brand.intro}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/topics"
                  className="px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  看看我们在聊什么
                </Link>
                <a
                  href="#contact"
                  className="px-5 py-3 rounded-md border border-foreground-900 text-foreground-900 text-sm whitespace-nowrap hover:bg-foreground-900 hover:text-background-50 transition-colors"
                >
                  联系我们
                </a>
              </div>
            </div>
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20warm%20workspace%20with%20notes%20and%20sticky%20papers%20in%20coral%20amber%20and%20mint%20green%2C%20cozy%20editorial%20illustration%2C%20soft%20daylight%2C%20no%20text&width=1000&height=800&seq=gt-about-team&orientation=landscape"
                alt="瓜田福利社 内容团队工作氛围图"
                title="瓜田福利社 团队"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {stats.map((item) => (
              <div key={item.id} className="px-4 py-5 rounded-lg bg-background-100 border border-background-200">
                <p className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {item.value}
                  <span className="text-sm ml-1 text-foreground-500">{item.unit}</span>
                </p>
                <p className="mt-1 text-xs md:text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <SectionHeading eyebrow="网站定位" title="我们想站在什么位置" align="center" />
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {positions.map((item) => (
                <div key={item.id} className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-xl text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <SectionHeading
                eyebrow="发展理念"
                title="把节奏放慢一点，把话说清楚一点"
                desc="热度来得快去得也快，我们更在意一条内容在半年后是否还站得住。"
              />
              <ol className="mt-8 space-y-6">
                {milestones.map((item) => (
                  <li key={item.id} className="flex gap-4">
                    <span className="shrink-0 w-16 font-heading text-lg text-primary-600">{item.year}</span>
                    <p className="text-sm text-foreground-700 leading-relaxed pt-1">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3 className="font-heading text-2xl text-foreground-950">内容原则</h3>
              <p className="mt-2 text-sm text-foreground-600">四条我们一直在执行的标准。</p>
              <div className="mt-6 space-y-4">
                {principles.map((item, index) => (
                  <div
                    key={item.id}
                    className="rounded-lg bg-background-50 border border-background-200 p-5 flex gap-4"
                  >
                    <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 font-heading">
                      {index + 1}
                    </span>
                    <span>
                      <span className="block font-heading text-lg text-foreground-950">{item.title}</span>
                      <span className="block mt-1 text-sm text-foreground-600 leading-relaxed">{item.desc}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading eyebrow="用户口碑" title="来自评论区的真实反馈" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((item) => (
              <blockquote
                key={item.id}
                className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6 flex flex-col"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-300 mb-3"></i>
                <p className="text-sm text-foreground-700 leading-relaxed flex-1">{item.text}</p>
                <footer className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 text-sm">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <SectionHeading
                eyebrow="联系方式"
                title="想聊点什么，随时找我们"
                desc="无论是内容建议、活动规则疑问还是话题投稿，我们都会认真看每一条留言。"
              />
              <ul className="mt-8 space-y-5">
                <li className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 text-primary-600 text-lg">
                    <i className="ri-mail-line"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">联系邮箱</span>
                    <a href={`mailto:${brand.email}`} className="text-sm text-foreground-900 hover:text-primary-600">
                      {brand.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 text-secondary-700 text-lg">
                    <i className="ri-time-line"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">服务时间</span>
                    <span className="text-sm text-foreground-900">{brand.serviceTime}</span>
                  </span>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-50 text-accent-700 text-lg">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">线上运营</span>
                    <span className="text-sm text-foreground-900">内容与活动均为线上进行，暂无到访安排</span>
                  </span>
                </li>
              </ul>
            </div>
            <ContactForm />
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}